/**
 * ¿Está esta pestaña corriendo una versión vieja de la aplicación?
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL CASO (21-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * Se desplegó un arreglo de cumplimiento —el botón «Bloquear» no bloqueaba de verdad
 * en Meta— y Rodrigo pulsó los tres bloqueos cuatro minutos después. Los tres salieron
 * con el comportamiento viejo: su pestaña llevaba abierta desde antes del despliegue.
 * Tras recargar, los mismos clics funcionaron.
 *
 * `index.html` va con `no-cache, no-store`, así que **una recarga normal basta**. Lo
 * que faltaba era que algo la pidiera.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * HABÍA DOS MECANISMOS Y NINGUNO PODÍA FUNCIONAR
 * ───────────────────────────────────────────────────────────────────────────────
 *   · `UpdateBanner` escuchaba `updatefound` sobre el service worker — pero el SW
 *     registrado es un KILL-SWITCH que se desregistra a sí mismo en su `activate`.
 *     No queda ningún SW persistente sobre el que disparar.
 *   · `useVersionCheck` comparaba `CURRENT_VERSION = '1.0.2'`, una constante escrita
 *     a mano que no cambiaba con el despliegue, y sólo en el montaje.
 *
 * Los dos se retiran en el mismo cambio. Dejarlos sería dejar dos señales de que el
 * problema está resuelto — «lo que no se lee no avisa», aplicado a una protección.
 *
 * ⚠️ EL KILL-SWITCH SW NO SE TOCA: sigue haciendo falta para desalojar workbox
 * viejos, y esto no depende de él.
 */

/** Lo que se compiló DENTRO de este bundle. Lo pone `tools/sellar-build.mjs`. */
export const BUILD_ACTUAL = process.env.REACT_APP_BUILD || null;

/**
 * Cada cuánto se consulta mientras la pestaña está visible.
 *
 * MEDIA HORA, y el argumento es de coste, no de urgencia: lo que se evita no es un
 * sistema roto sino uno desactualizado —el operador trabaja con una versión que
 * funcionaba hace minutos—. Con varias pestañas por operador, un minuto serían miles
 * de peticiones diarias por un JSON de sesenta bytes. Media hora acota la ventana a
 * un orden de magnitud por debajo de la jornada, que es lo que importa.
 *
 * Y el periódico sólo tiene que atrapar la cola: los disparadores por evento cubren
 * el caso frecuente casi gratis.
 */
export const INTERVALO_MS = 30 * 60 * 1000;

/** Dos consultas separadas por menos de esto se consideran la misma. Ver `crearDetector`. */
export const ANTIRREBOTE_MS = 5 * 1000;

/**
 * ¿El sello servido delata una versión distinta de la que corre?
 *
 * DEVUELVE FALSE ANTE LA DUDA, y las tres razones son la misma: un banner que aparece
 * sin motivo enseña a ignorarlo.
 *   · sin `BUILD_ACTUAL` —un build sin sellar, o `npm start`— no hay con qué comparar.
 *   · sin `servido` —la petición falló, o el JSON no tiene la clave— tampoco.
 *   · iguales, evidentemente.
 */
export function hayVersionNueva(servido, actual = BUILD_ACTUAL) {
  if (!actual || typeof actual !== 'string') return false;
  if (!servido || typeof servido !== 'string') return false;
  return servido !== actual;
}

/**
 * Crea el detector. Se separa del hook de React para poder probarlo sin montar nada.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * DOS DISPARADORES POR EVENTO, Y HACEN FALTA LOS DOS
 * ───────────────────────────────────────────────────────────────────────────────
 *   · `visibilitychange` — cubre volver a la pestaña desde otra pestaña.
 *   · `focus` en `window` — cubre lo que el anterior NO ve: el operador cambia a otra
 *     aplicación con el navegador todavía visible en otro monitor, y al volver hace
 *     clic en la ventana. Ahí `visibilityState` nunca dejó de ser `visible`, así que
 *     `visibilitychange` no dispara. Mismo coste, un caso más.
 *
 * ⚠️ LOS DOS LLEGAN JUNTOS al volver de otra pestaña, y por eso existe el
 * antirrebote: sin él, ese caso —el más frecuente— haría DOS peticiones por cada
 * retorno. Es el mismo defecto que este repositorio acaba de documentar en el doble
 * bloqueo, y aquí es evitable de antemano.
 *
 * @param {object} opciones
 * @param {(url:string, init:object) => Promise<any>} opciones.fetchImpl
 * @param {() => number} opciones.ahora
 * @param {(build:string) => void} opciones.alDetectar - se llama UNA vez
 */
export function crearDetector({
  fetchImpl,
  ahora = () => Date.now(),
  alDetectar,
  intervaloMs = INTERVALO_MS,
  // El sello de ESTE bundle. Entra por parámetro —con el real por defecto— para poder
  // probar el detector sin tocar `process.env`: en el entorno de test no hay sello, y
  // sin este hueco los casos de «sí avisa» sólo se podrían escribir manipulando el
  // entorno entre imports, que es frágil y oscurece lo que se está probando.
  buildActual = BUILD_ACTUAL,
} = {}) {
  let ultimaConsulta = 0;
  let yaAvisado = false;

  async function consultar(motivo) {
    if (yaAvisado) return { saltada: true, motivo: 'ya-avisado' };
    const t = ahora();
    if (t - ultimaConsulta < ANTIRREBOTE_MS) return { saltada: true, motivo: 'antirrebote' };
    ultimaConsulta = t;
    try {
      // `no-store` y no `no-cache`: el segundo permite servir del caché tras
      // revalidar, y aquí se quiere el fichero, siempre.
      const r = await fetchImpl('/version.json', { cache: 'no-store' });
      const j = await r.json();
      if (hayVersionNueva(j?.build, buildActual)) {
        yaAvisado = true;
        if (alDetectar) alDetectar(j.build);
        return { nueva: true, build: j.build, motivo };
      }
      return { nueva: false, motivo };
    } catch {
      // Una consulta fallida no dice nada: ni avisa ni se reintenta antes de tiempo.
      return { error: true, motivo };
    }
  }

  return { consultar, intervaloMs };
}
